/**
 * DataTable — a thin, generic wrapper over the recurring table styling.
 *
 * Columns describe header label, alignment, and a cell renderer. Keeps the
 * warm-neutral row borders, header treatment, and hover from the design system
 * in one place so every list page looks identical. Generic over the row type.
 */
import type { CSSProperties, ReactNode } from 'react';
import { colors } from '../theme';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  /** Stable key for the column. */
  key: string;
  header: ReactNode;
  /** Cell renderer for a row. */
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Optional fixed width (px or css). */
  width?: number | string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  /** Stable row key. */
  rowKey: (row: T) => string;
  /** Optional row click handler (whole-row affordance). */
  onRowClick?: (row: T) => void;
  /** Shown when `rows` is empty. */
  emptyTitle?: string;
  emptyDescription?: string;
  style?: CSSProperties;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  style,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <div style={{ width: '100%', overflowX: 'auto', ...style }}>
      <table style={table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  ...th,
                  textAlign: c.align ?? 'left',
                  width: c.width,
                }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? rowClickable : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ ...td, textAlign: c.align ?? 'left' }}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};
const th: CSSProperties = {
  padding: '10px 12px',
  borderBottom: `2px solid ${colors.surfaceAlt}`,
  color: colors.textSecondary,
  fontWeight: 600,
  fontSize: '0.78rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap',
};
const td: CSSProperties = {
  padding: '12px',
  borderBottom: `1px solid ${colors.border}`,
  color: colors.textPrimary,
  verticalAlign: 'middle',
};
const rowClickable: CSSProperties = { cursor: 'pointer' };
